import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ResizeCorner } from '../../../utils/stickyResize';

function relativeCorners(outset: number): { corner: ResizeCorner; left: string | number; top: string | number }[] {
  // Positive outset pushes dots outward (e.g. onto a CSS outline with outline-offset).
  const lo = outset === 0 ? 0 : -outset;
  const hi = outset === 0 ? '100%' : `calc(100% + ${outset}px)`;
  return [
    { corner: 'tl', left: lo, top: lo },
    { corner: 'tr', left: hi, top: lo },
    { corner: 'br', left: hi, top: hi },
    { corner: 'bl', left: lo, top: hi },
  ];
}

/** Optional resize-handle wiring passed into StickyNote / TextLabel. */
export interface ItemResizeHandleProps {
  /** When true, render corner resize dots on this item. */
  showResizeHandles?: boolean;
  onResizePointerDown?: (corner: ResizeCorner, e: React.PointerEvent<HTMLDivElement>) => void;
  onResizePointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onResizePointerUp?: () => void;
  onResizePointerCancel?: () => void;
  onResizeLostPointerCapture?: () => void;
}

export interface ResizeCornerHandlesProps {
  /**
   * Extra board-px outside the parent box — use to sit on a selection outline
   * that has outline-offset (TextLabel uses LABEL_OUTLINE_OFFSET_PX).
   */
  outset?: number;
  /** Canvas zoom — handle screen size stays constant via 10/scale. */
  scale: number;
  selectionColor: string;
  onCornerPointerDown: (corner: ResizeCorner, e: React.PointerEvent<HTMLDivElement>) => void;
  onCornerPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onCornerPointerUp: () => void;
  onCornerPointerCancel?: () => void;
  onCornerLostPointerCapture?: () => void;
}

/**
 * Four corner resize dots, pinned to the parent element's box (and optional outset).
 * Render inside StickyNote / TextLabel so dots track layout / fit-content automatically.
 */
export default function ResizeCornerHandles({
  outset = 0,
  scale,
  selectionColor,
  onCornerPointerDown,
  onCornerPointerMove,
  onCornerPointerUp,
  onCornerPointerCancel,
  onCornerLostPointerCapture,
}: ResizeCornerHandlesProps): React.ReactElement {
  const theme = useTheme();
  const s  = scale;
  const hs = 10 / s;

  return (
    <>
      {relativeCorners(outset).map(({ corner, left, top }) => {
        const cursor = (corner === 'tl' || corner === 'br') ? 'nwse-resize' : 'nesw-resize';
        return (
          <Box
            key={corner}
            onPointerDown={(e) => onCornerPointerDown(corner, e)}
            onPointerMove={onCornerPointerMove}
            onPointerUp={onCornerPointerUp}
            onPointerCancel={onCornerPointerCancel}
            onLostPointerCapture={onCornerLostPointerCapture}
            onContextMenu={(e) => e.preventDefault()}
            sx={{
              position: 'absolute', left, top,
              width: hs, height: hs,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%', background: theme.palette.background.paper,
              border: `${2 / s}px solid ${selectionColor}`,
              boxShadow: `0 ${1 / s}px ${4 / s}px rgba(0,0,0,0.2)`,
              cursor, zIndex: 600, touchAction: 'none', pointerEvents: 'auto',
            }}
          />
        );
      })}
    </>
  );
}
